/**
 * ToolTrace — the collapsible trace of one turn's tool calls.
 *
 * Replaces `ThoughtBlock` in the AG-UI panel. Identical chrome and CSS classes
 * (`chat-msg__thought*`, `thought-item*` — see ChatPanel.css), so this is not a
 * visual change; what changes is where the data comes from.
 *
 * ThoughtBlock's input was a tagged string we built ourselves
 * (`[TOOL_START]name\n k=v[/TOOL][TOOL_DONE]name|success[/TOOL]`) and then
 * parsed back apart with ~90 lines of regex + merge logic. Both ends of that
 * encode/decode round trip were ours, and the string was assembled in three
 * different places (live mirror, streaming subscriber, DDB history) that had to
 * agree on its shape. CopilotKit already carries the structured form — a tool
 * call with parsed `parameters` and a real `status` — so the rows here take that
 * directly, via `useDefaultRenderTool`'s wildcard registration.
 *
 * Two behaviours deliberately NOT carried over from ThoughtBlock:
 *
 *   • `error` status. ThoughtBlock could render `✗`, but nothing in the AG-UI
 *     path ever emitted `[TOOL_DONE]name|error` — every producer hard-coded
 *     `|success`. The state was unreachable, so it is gone rather than
 *     reproduced. CopilotKit hands us `result` on completion, which is where a
 *     real failure signal would come from if we want one.
 *   • `FILE_OUTPUT` / `INTERRUPT` rows. Only the legacy SSE panel produces those
 *     markers (ChatPanel.tsx emits them into its own thought buffer); the AG-UI
 *     path never did. File outputs here render as `FileOutputCards` and
 *     approvals as their own panels.
 */

import { useTranslation } from 'react-i18next';
import SafeTrans from '../../../i18n/SafeTrans';
import { useState } from 'react';
import { Icon } from '@amzn/awsui-components-react';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';

/** CopilotKit's tool-call lifecycle (`DefaultRenderProps['status']`). */
export type ToolTraceStatus = 'inProgress' | 'executing' | 'complete';

export interface ToolTraceRowProps {
  name: string;
  /** Parsed tool arguments. CopilotKit gives these already JSON.parse'd. */
  parameters: unknown;
  status: ToolTraceStatus;
}

/**
 * Render a tool call's arguments as the single `k=v, k=v` line the trace shows.
 *
 * Code-interpreter calls are special-cased: their `code` is the whole point of
 * looking at the row, and a one-line `k=v` rendering of a Python block is
 * unreadable. Same special case `buildToolStartBody` had — kept because it is a
 * real readability win, moved here because this is now the only place that
 * formats a tool call for display.
 */
export function formatToolArgs(parameters: unknown): {
  detail: string;
  code?: string;
  codeLang?: string;
} {
  if (!parameters || typeof parameters !== 'object' || Array.isArray(parameters)) {
    return { detail: '' };
  }
  const args = parameters as Record<string, unknown>;
  const ciiAction = (args.code_interpreter_input as Record<string, unknown> | undefined)
    ?.action as Record<string, unknown> | undefined;
  const code = ciiAction?.code;
  if (typeof code === 'string' && code) {
    return { detail: '', code, codeLang: String(ciiAction?.language ?? 'python') };
  }
  const detail = Object.entries(args)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(', ');
  return { detail };
}

/** One tool call inside the trace. */
export function ToolTraceRow({ name, parameters, status }: ToolTraceRowProps) {
  const { detail, code, codeLang } = formatToolArgs(parameters);
  const done = status === 'complete';
  return (
    <div className="thought-item">
      <div className="thought-item__header">
        <span className={`thought-item__icon thought-item__icon--${done ? 'success' : 'running'}`}>
          {done ? '✓' : <StatusIndicator type="loading" />}
        </span>
        <span className="thought-item__name">{name}</span>
      </div>
      {detail && <div className="thought-item__detail">{detail}</div>}
      {code && (
        <div className="thought-item__code">
          <pre className="thought-item__code-block" data-lang={codeLang}>
            <code>{code}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

/** A process-narration line in the trace: muted text, no icon or status. */
export function ToolTraceNote({ text }: { text: string }) {
  return (
    <div className="thought-item thought-item--note">
      <div className="thought-item__detail thought-item__note-text">{text}</div>
    </div>
  );
}

interface ToolTraceProps {
  /** The rendered rows — `useRenderToolCall` output for this turn's tool calls. */
  children: React.ReactNode;
  /** Open and labelled "Thinking..." while the turn is still streaming. */
  isStreaming?: boolean;
}

/**
 * The collapsible shell around a turn's tool rows.
 *
 * Collapsed by default once the turn finishes: the trace is diagnostic detail,
 * not the answer. Open by default while streaming so the user can see progress,
 * but only by default — the toggle works during a run too. It used to be inert
 * then, on the theory that collapsing a live trace hides the only evidence
 * anything is happening; the header's spinner and "Thinking..." stay visible
 * when collapsed, so that evidence survives, and a long trace pinned open pushed
 * the streaming answer out of view.
 *
 * A choice made mid-stream outlives the run: collapse it and it stays collapsed,
 * reopen it and it stays open. Untouched, it follows the defaults above. That
 * state is per instance, so a collapse on the in-flight placeholder (before the
 * turn's first assistant row exists) does not carry over to the row's own trace.
 */
export default function ToolTrace({ children, isStreaming }: ToolTraceProps) {
  const { t } = useTranslation('chat');
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const open = manualToggle ?? !!isStreaming;

  const handleToggle = () => setManualToggle(prev => !(prev ?? !!isStreaming));

  return (
    <div className={`chat-msg__thought ${open ? 'chat-msg__thought--open' : ''}`}>
      <button className="chat-msg__thought-toggle" onClick={handleToggle} aria-expanded={open}>
        <span className="chat-msg__thought-status-icon">
          {isStreaming ? (
            <StatusIndicator type="in-progress" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#0f141a"/><path d="M5.5 9.5L7.5 11.5L12.5 6.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </span>
        <span className="chat-msg__thought-label">
          {isStreaming ? t('thought.thinking') : t('thought.completed')}
        </span>
        <span className="chat-msg__thought-chevron"><Icon name={open ? 'angle-up' : 'angle-down'} /></span>
      </button>
      {open && (
        <div className="chat-msg__thought-body">
          {isStreaming && (
            <div className="chat-msg__thought-hint">
              <SafeTrans
                t={t}
                i18nKey="thought.hint"
                components={{ icon: <img src="/icons/icon-chat-add.svg" alt={t('thought.newChatAlt')} className="chat-msg__thought-hint-icon" /> }}
              />
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
